import { FunctionComponent, useMemo } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import { InterestLayers, possibleLayers } from '@/constants/diagnoses'
import { DiagnosisDTO } from '@/hooks/queries/use-get-physiological-diagnoses'
import { useCompanies } from '@/hooks/use-companies'

import { CreatePulverizationMapContextProvider } from '../contexts/create-pulverization-map-context'
import { PlantingFailure } from '../cultures/planting-failure'
import { Soybean } from '../cultures/soybean'
import { Weed } from '../cultures/weed'

export interface TreatmentState {
  soybean?: {
    physiology?: { layer?: InterestLayers; diagnosis: DiagnosisDTO }
  }
}

type TreatmentValue = (value: {
  state?: TreatmentState
  id?: string
  culture?: string
  treatment?: string
  validation?: boolean
  searchParams?: URLSearchParams
}) => {
  valid: boolean
  Component: FunctionComponent
}

interface Treatments {
  [key: string]: TreatmentValue
}

interface Cultures {
  [key: string]: Treatments
}

interface DiagnosisViewParams {
  culture: string
  treatment: string
  id: string
  companyId: string
}

const cultures: Cultures = {
  soybean: {
    physiology: ({ id, validation, searchParams }) => {
      const selectedLayer = searchParams?.get('layer') as InterestLayers
      const isValidLayer = possibleLayers.includes(selectedLayer)
      return {
        valid: Boolean(validation) && isValidLayer,
        Component: () => <Soybean id={id!} selectedLayer={selectedLayer} />,
      }
    },
    weed: ({ id, validation }) => ({
      valid: Boolean(validation),
      Component: () => (
        <CreatePulverizationMapContextProvider
          culture='soybean'
          treatment='weed'
          diagnosisId={id}
        >
          <Weed />
        </CreatePulverizationMapContextProvider>
      ),
    }),
  },
  cane: {
    weed: ({ id, validation }) => ({
      valid: Boolean(validation),
      Component: () => (
        <CreatePulverizationMapContextProvider
          culture='cane'
          treatment='weed'
          diagnosisId={id}
        >
          <Weed />
        </CreatePulverizationMapContextProvider>
      ),
    }),
    planting_failure: ({ id, validation }) => ({
      valid: Boolean(validation),
      Component: () => (
        <PlantingFailure
          culture='cane'
          treatment='planting_failure'
          diagnosisId={id!}
        />
      ),
    }),
  },
}

export function useDiagnosisViewPage() {
  const location = useLocation()
  const { selectedCompany } = useCompanies()

  const { culture, treatment, id, companyId }: DiagnosisViewParams =
    useParams() as any

  const state = location.state as TreatmentState

  const [searchParams] = useSearchParams()

  return useMemo(() => {
    const isSameCompany = selectedCompany.id === Number(companyId)

    const currentCulture = cultures[culture]
    const currentTreatment =
      currentCulture &&
      currentCulture[treatment]?.({
        id,
        state,
        culture,
        treatment,
        validation: isSameCompany,
        searchParams,
      })

    const CurrentComponent = currentTreatment?.Component

    return { isValid: currentTreatment?.valid, CurrentComponent }
  }, [state, selectedCompany.id, searchParams])
}
